import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend,
  LineElement, PointElement,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, LineElement, PointElement);

const SLA_STATUS = {
  CONFORME:     { bg: '#DCFCE7', color: '#15803D', label: 'Conforme'      },
  A_RISQUE:     { bg: '#FFEDD5', color: '#C2410C', label: 'À risque'      },
  NON_CONFORME: { bg: '#FEE2E2', color: '#B91C1C', label: 'Non conforme'  },
};

export default function SlaReport({ data }) {
  if (!data) {
    return (
      <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: 14, padding: 40 }}>
        Chargement du rapport SLA…
      </div>
    );
  }

  const { kpis, services = [], period } = data;

  // Données graphique
  const labels  = services.map((s) => s.name.replace('Amplitude ', 'Ampl. ').substring(0, 16));
  const avails  = services.map((s) => parseFloat(s.availability || 100));
  const targets = services.map((s) => parseFloat(s.sla_target));
  const barColors = avails.map((v, i) =>
    v >= targets[i] ? '#16A34A' : v >= targets[i] - 2 ? '#EA580C' : '#DC2626'
  );

  const chartData = {
    labels,
    datasets: [
      {
        type: 'bar',
        label: 'Disponibilité réelle (%)',
        data: avails,
        backgroundColor: barColors,
        borderRadius: 3,
      },
      {
        type: 'line',
        label: 'SLA cible (%)',
        data: targets,
        borderColor: '#1B2A4A',
        borderWidth: 1.5,
        borderDash: [4, 3],
        pointRadius: 3,
        pointBackgroundColor: '#1B2A4A',
        fill: false,
      },
    ],
  };

  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${c.parsed.y.toFixed(2)}%` } },
    },
    scales: {
      x: { ticks: { font: { size: 9 }, color: '#64748B' }, grid: { display: false } },
      y: {
        min: Math.max(0, Math.min(...avails) - 2),
        max: 100.5,
        ticks: { font: { size: 9 }, color: '#94A3B8', callback: (v) => `${v}%` },
        grid: { color: '#F1F5F9' },
      },
    },
  };

  return (
    <div>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#1B2A4A',
                      textTransform: 'uppercase', letterSpacing: 1,
                      display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 3, height: 12, background: '#C9A84C', borderRadius: 2 }} />
          Rapport SLA — {period || '—'}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 16 }}>
        {[
          { val: `${kpis?.globalAvailability ?? '—'}%`, label: 'Disponibilité globale', color: '#16A34A' },
          { val: kpis?.totalIncidents ?? 0,              label: 'Incidents total',        color: '#EA580C' },
          { val: `${kpis?.avgMttrMinutes ?? 0} min`,     label: 'MTTR moyen',             color: '#1B2A4A' },
          { val: kpis?.servicesConforme ?? 0,            label: 'Services conformes',     color: '#16A34A' },
        ].map(({ val, label, color }) => (
          <div key={label} style={{ background: '#fff', border: '1px solid #E2E8F0',
                                    borderRadius: 9, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color, marginBottom: 2 }}>{val}</div>
            <div style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: .5 }}>
              {label}
            </div>
          </div>
        ))}
      </div>

      {/* Graphique */}
      <div style={{ marginBottom: 16, height: 160, background: '#fff',
                    border: '1px solid #E2E8F0', borderRadius: 9, padding: '12px 12px 8px' }}>
        <Bar data={chartData} options={chartOpts} />
      </div>

      {/* Tableau */}
      <table style={{ width: '100%', borderCollapse: 'collapse',
                      background: '#fff', border: '1px solid #E2E8F0',
                      borderRadius: 9, overflow: 'hidden', fontSize: 11 }}>
        <thead>
          <tr style={{ background: '#1B2A4A' }}>
            {['Service','Catégorie','Dispo. réelle','SLA cible','Barre','Incidents','Statut'].map((h) => (
              <th key={h} style={{ padding: '8px 12px', textAlign: 'left',
                                   color: 'rgba(255,255,255,.8)', fontSize: 10,
                                   fontWeight: 600, textTransform: 'uppercase', letterSpacing: .5 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {services.map((svc, i) => {
            const st   = SLA_STATUS[svc.sla_status] || SLA_STATUS.CONFORME;
            const avail = parseFloat(svc.availability || 100);
            const barColor = avail >= svc.sla_target ? '#16A34A'
                           : avail >= svc.sla_target - 2 ? '#EA580C' : '#DC2626';
            return (
              <tr key={svc.id} style={{ background: i % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1B2A4A' }}>{svc.name}</td>
                <td style={{ padding: '8px 12px', color: '#64748B' }}>{svc.category}</td>
                <td style={{ padding: '8px 12px', fontWeight: 700,
                             color: avail >= svc.sla_target ? '#16A34A' : '#DC2626' }}>
                  {avail.toFixed(2)}%
                </td>
                <td style={{ padding: '8px 12px', color: '#64748B' }}>{svc.sla_target}%</td>
                <td style={{ padding: '8px 12px' }}>
                  <div style={{ width: '100%', height: 6, background: '#F1F5F9',
                                borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 3,
                                  width: `${Math.min(avail, 100)}%`, background: barColor }} />
                  </div>
                </td>
                <td style={{ padding: '8px 12px', color: '#334155', textAlign: 'center' }}>
                  {svc.total_incidents || 0}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px',
                                 borderRadius: 12, textTransform: 'uppercase',
                                 background: st.bg, color: st.color }}>
                    {st.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
